import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from "@angular/forms";
import {HttpClient} from "@angular/common/http";
import {PortalService} from "../services/portal.service";
import {ElementService} from "../services/element.service";
import {ValidationService} from "../services/validation.service";
import {Observable} from "rxjs";
import {TranslateService} from "@ngx-translate/core";
import {map} from "rxjs/operators";

@Component({
    selector: 'app-dialogform',
    templateUrl: './dialogform.component.html',
    standalone: false
})
export class DialogformComponent implements OnInit {

  /**
   * prefix for all textkeys that are used inside this component.
   */
  @Input() textkeyPrefix: string;

  /**
   * Extra Postfix that is optional on every text
   */
  @Input() textkeyPostfix: string = "";

  /**
   * additional data for the regisztration of the user
   */
  @Input() registrationData: any = {};

  /**
   * Download URL for the report. If not set, download is not shown
   */
  @Input() downloadUrl: string = null;

  /**
   * textkey for the copyright. If set to null, no copyright is shown
   */
  @Input() copyright: string = '.dialog.copyright';

  /**
   * URL for the image that should be shown near the list
   */
  @Input() advisorImagePath: string = '/moc/naechsteschritte-dialog.jpeg';

  /**
   * Defines the breakpoint for the large viewport. Can be overwritten for caeses where this element
   * is near a sidebar and needs to show the large viewpoint later
   */
  @Input() largeBreakpoint: string = 'large';

  /**
   * Defines the breakpoint for the medium viewport. Can be overwritten for caeses where this element
   * is near a sidebar and needs to show the large viewpoint later
   */
  @Input() mediumBreakpoint: string = 'medium';

  /**
   * Eventimitter that is called, if a registration is finished. Must not be successfull but a result from the backend was received.
   */
  @Output() onDialogRegister: EventEmitter<string> = new EventEmitter<string>();

  /**
   * The form errors element
   */
  @ViewChild('formErrors') formErrors: ElementRef;

  /**
   * The result wrapper element
   */
  @ViewChild('resultWrapper') resultWrapper: ElementRef;

  /**
   * Set to true if the submit button is activated
   */
  submitted: boolean = false;
  /**
   * List of resultcodes from the api that we interprete as success
   */
  readonly apiSuccessResults: string[] = ['ok', 'duplicate', 'eventmail'];

  /**
   * Formgroup for input validation
   */
  form: UntypedFormGroup;

  /**
   * Toggle for showing an extra checkbox for the terms
   */
  showCheckbox: boolean = false;

  /**
   * toggle if the terms are selected or not.
   */
  terms: boolean = true;

  /**
   * error textkey
   */
  error: string = null;

  /**
   * Result of the registration
   */
  result: string = null;

  /**
   * toggle if this is an internal project or not.
   */
  internalPortal: boolean;

  /**
   * fields for salutation, name and firstname should be visible
   */
  showName: boolean = false;

  /**
   * fields for street, streetnumber, postalcode, city should be visible
   */
  showAddress: boolean = false;

  /**
   * field for phone number should be visible
   */
  showPhone: boolean = false;

  /**
   * field for dropdown with multiple selectable options should be visible
   */
  showMulitselect: boolean = false;

  /**
   * list of option count for the multiselect dropdown (10 allowed - add more if needed)
   */
  multiselectOptions: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  /**
   * translated & validated options for the multiselect dropdown, consumed via the async pipe in the template
   */
  multiselectOptions$: Observable<string[]>;

  /**
   * field for consultant_name should be visible
   */
  showConsultantName: boolean = false;

  /**
   * Options for the salutaion dropdown
   */
  salutationOptions = ['frau', 'herr', 'divers'];

  /**
   * indicates whether mandatory fields are marked.
   */
  markMandatoryFields: boolean = false;

  /**
   * list of mandatory contactfields
   */
  mandatoryFields: string[] = [];

  /**
   * specifies whether to use an external API instead of Broadmail.
   */
  useExternalApi: boolean = false;

  /**
   * Hide image
   */
  hideimage: boolean = false;

  /**
   * do not send a request, skip registration and show hint text after sending valid form
   */
  skipRegistration: boolean = false;

  /**
   * constructor
   */
  constructor(private http: HttpClient, private portalService: PortalService, private elementService: ElementService, private translateService: TranslateService) {}

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    // init the form
    this.form = new UntypedFormGroup({
      'dialogform-email': new UntypedFormControl('', [Validators.required, Validators.email])
    });

    // should be use a checkbox or terms link for the lead form
    this.showCheckbox = this.portalService.getConfigAsBool("embed.newsletter.checkbox");
    if(this.showCheckbox === true) {
      this.form.addControl('dialogform-terms', new UntypedFormControl('', [Validators.requiredTrue]))
    }

    this.mandatoryFields = this.portalService.getConfig('embed.newsletter.mandatory_fields')?
      this.portalService.getConfig('embed.newsletter.mandatory_fields').split(',') : [];

    // remove spacings from strings in mandatoryFiels array
    this.mandatoryFields = this.mandatoryFields?.map(field => field.trim());

    // should be used fields for salutation and names?
    this.showName = this.portalService.getConfigAsBool('embed.newsletter.name');
    if (this.showName === true) {
      ['salutation', 'firstname', 'lastname'].forEach(fieldName => {
        this.form.addControl('dialogform-' + fieldName, new UntypedFormControl('', this.mandatoryFields.indexOf(fieldName) > -1? Validators.required : []));
      });
    }

    // should the address fields be used?
    this.showAddress = this.portalService.getConfigAsBool('embed.newsletter.address');
    if (this.showAddress === true) {
      // format validators that apply regardless of whether the field is mandatory
      const addressFieldValidators: { [fieldName: string]: ValidatorFn[] } = {
        postalcode: [ValidationService.postalCodeOrEmpty],
        streetnumber: [Validators.min(0)]
      };
      ['street', 'streetnumber', 'postalcode', 'city'].forEach(fieldName => {
        const validators = [...(addressFieldValidators[fieldName] ?? [])];
        if (this.mandatoryFields.indexOf(fieldName) > -1) {
          validators.push(Validators.required);
        }
        this.form.addControl('dialogform-' + fieldName, new UntypedFormControl('', validators));
      });
    }

    // should the phone fields be used?
    this.showPhone = this.portalService.getConfigAsBool('embed.newsletter.phone');
    if (this.showPhone === true) {
      this.form.addControl('dialogform-phone', new UntypedFormControl('', this.mandatoryFields.indexOf('phone') > -1? Validators.required : []));
    }

    // should the multiselect fields be used?
    this.showMulitselect = this.portalService.getConfigAsBool('embed.newsletter.multiselect');
    if (this.showMulitselect === true) {
      this.form.addControl('dialogform-multiselect', new UntypedFormControl([], this.mandatoryFields.indexOf('multiselect') > -1? Validators.required : []));
      this.multiselectOptions$ = this.getMultiselectOptions();
    }

    this.showConsultantName = this.portalService.currentPortalId === 'wuestenrot_eb';
    if (this.showConsultantName === true) {
      this.form.addControl('dialogform-consultant_name', new UntypedFormControl('', []));
    }

    // should mandatory fields be marked
    this.markMandatoryFields = this.portalService.getConfigAsBool('embed.newsletter.mark_mandatory_fields');

    this.internalPortal = this.portalService.isInternalPortal();
    this.useExternalApi = this.portalService.getConfigAsBool('embed.newsletter.use_external_api');
    this.hideimage = this.portalService.getConfigAsBool('embed.newsletter.hideimage');
    this.skipRegistration = this.portalService.getConfigAsBool('embed.newsletter.norequest');

    // set init value of submit button to false
    this.submitted = false;
  }

  /**
   * click on submit button
   */
  submit(): void {
    this.submitted = true;
    this.error = null;
    if(this.form.valid === false) {
      if(this.showCheckbox === true && this.form.get('dialogform-terms').valid === false) {
        this.error = 'terms';
      }
      // check if we have mandatory fields
      else if(this.mandatoryFields.length > 0) {
        this.mandatoryFields.forEach(field => {
          // if they are not valid show error message
          if (this.form?.get('dialogform-' + field)?.valid === false) {
            this.error = "emptyfields";
          }
          // if mandatory fields are valid but email field is not, show different error message
          if (this.form?.get('dialogform-' + field)?.valid === true && this.form.get('dialogform-email').valid === false) {
            this.error = "email";
          }
        });
      }
      // fallback: form is valid except email field
      else {
        this.error = 'email';
      }
      // scroll to error
      setTimeout(() => {
        this.elementService.scrollToElement(this.formErrors.nativeElement);
      }, 200);
      return;
    }

    const url = this.useExternalApi ? '/dialog/registration/external' : '/dialog/registration';
    if (this.useExternalApi === true) {
      this.registrationData.newsletter = true;
    }

    // should the name be visible
    if (this.showName === true) {
      this.registrationData.salutation = this.form.get('dialogform-salutation').value;
      this.registrationData.firstname = this.form.get('dialogform-firstname').value;
      this.registrationData.lastname = this.form.get('dialogform-lastname').value;
    }

    if (this.showConsultantName === true) {
      this.registrationData.consultant_name = this.form.get('dialogform-consultant_name').value;
    }

    // save data and show result
    if (this.skipRegistration) {
      // case 1: save contact data in database all_portal_contact, do not sending data to broadmail or external api
      const contactData: any = {...this.registrationData, ...{email: this.form.get('dialogform-email').value}};
      if (this.showCheckbox === true) {
        contactData.terms = this.form.get('dialogform-terms').value;
      }
      if (this.showAddress === true) {
        contactData.street = this.form.get('dialogform-street').value;
        contactData.streetnumber = this.form.get('dialogform-streetnumber').value;
        contactData.postalcode = this.form.get('dialogform-postalcode').value;
        contactData.city = this.form.get('dialogform-city').value;
      }
      if (this.showPhone === true) {
        contactData.phone = this.form.get('dialogform-phone').value;
      }
      if (this.showMulitselect === true) {
        contactData.multiselect = this.form.get('dialogform-multiselect').value;
      }

      this.http.post('/dialog/registration/store', contactData).subscribe(response => {
        if (response.hasOwnProperty('result') && response['result'] === 'ok') {
          this.result = 'skipregistration';
          // scroll to form-wrapper
          setTimeout(() => {
            this.elementService.scrollToElement(this.resultWrapper.nativeElement);
          }, 200);
        } else {
          this.internalError('internal', response);
        }
        this.onDialogRegister.emit(this.result);
      },
      error => this.internalError(error));
    }
    else {
      // case 2: standard case: send data to broadmail / external api
      this.http.post(url, {...this.registrationData, ...{email: this.form.get('dialogform-email').value}}).subscribe(response => {
        if (response.hasOwnProperty('result')) {
          if (this.apiSuccessResults.indexOf(response['result']) !== -1) {
            this.result = response['result'];
            // scroll to form-wrapper
            setTimeout(() => {
              this.elementService.scrollToElement(this.resultWrapper.nativeElement);
            }, 200);
          } else {
            this.internalError(response['result']);
          }
          this.onDialogRegister.emit(this.result);
        } else {
          this.internalError('internal', response);
          this.onDialogRegister.emit('internalerror');
        }
      },
      error => this.internalError(error));
    }
  }

  /**
   * Result of registration was an internal error
   * @param error
   * @param message
   */
  internalError(error: string = 'internal', message: any = null): void {
    if(typeof error === "object") {
      // in some error cases, the error is an object (i.e. 500 eror from backend)
      error = 'internal';
    }
    this.error = error;
    setTimeout(() => {
      this.elementService.scrollToElement(this.formErrors.nativeElement);
    }, 200);
    if(message) {
      throw Error('could not register email. ' + JSON.stringify(message));
    }
  }


  /**
   * get the translated multiselect options, if textkeys are provided and not empty string show 1-10 options
   * @private
   */
  private getMultiselectOptions(): Observable<string[]> {
    const keys = this.multiselectOptions.map(option => this.textkeyPrefix + '.dialogform.multiselect.option.' + option);

    return this.translateService.get(keys).pipe(
      map(translations => keys.reduce<string[]>((options, key) => {
        const translatedText = translations[key];
        if (translatedText !== undefined && translatedText !== null && translatedText.trim() !== '' && translatedText !== key) {
          options.push(translatedText);
        }
        return options;
      }, []))
    );
  }
}
